import type { RouteLocationRaw, RouteRecordRaw } from 'vue-router'

// Public surface for cross-feature consumers (e.g. stakeholders).
// Internal admin code uses relative imports.
export {
  uploadProfileImage,
  type UploadProfileImageResponse,
  type UploadResponse
} from '@gat-ship/tenant-users'

// Administration modal — quick access to administration registries from
// anywhere in the app, driven by the central modal stack
// (`?modals=admin:<panel>`). Mounted once via the app's `ModalStackHost`.
export { default as AdministrationModal } from './components/AdministrationModal.vue'

/**
 * Maps the leading segment of a legacy `/admin/<segment>` URL to the modal
 * panel slug it now opens. Keeps old bookmarks/links working after the route
 * views were retired (central-modal-host Phase 7).
 */
const ADMIN_PATH_TO_PANEL: Record<string, string> = {
  agency: 'agency-structure',
  company: 'companies',
  'tenant-users': 'tenant-users',
  'access-control': 'access-roles',
  monetary: 'currencies',
  settings: 'agency-settings',
  'custom-fields': 'custom-fields',
  portcall: 'estimated-time-names'
}

/**
 * The legacy `/admin/*` route views are retired; administration now lives in
 * the central modal stack. This redirect folds any legacy `/admin*` URL into
 * the equivalent `?modals=admin:<panel>` deep-link on the app home, so old
 * bookmarks keep opening the right panel.
 */
export function createAdministrationRoutes(
  startPath: string
): readonly RouteRecordRaw[] {
  const redirect = (to: { path: string }): RouteLocationRaw => {
    const rest = to.path.slice(startPath.length).replace(/^\/+/, '')
    const first = rest.split('/')[0] ?? ''
    const panel = ADMIN_PATH_TO_PANEL[first] ?? 'agency-structure'
    return { path: '/portcalls', query: { modals: `admin:${panel}` } }
  }
  return [
    { path: startPath, redirect },
    { path: `${startPath}/:pathMatch(.*)*`, redirect }
  ]
}
